import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@repo/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-95",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-elevation-1 hover:shadow-elevation-2 hover:bg-primary/90 [&_svg]:text-primary-foreground",
        destructive:
          "bg-destructive text-destructive-foreground shadow-elevation-1 hover:shadow-elevation-2 hover:bg-destructive/90 [&_svg]:text-destructive-foreground",
        danger:
          "bg-danger text-danger-foreground shadow-elevation-1 hover:shadow-elevation-2 hover:bg-danger-hover [&_svg]:text-danger-foreground",
        outline:
          "border border-input bg-surface shadow-sm hover:bg-primary hover:text-primary-foreground hover:border-primary hover:shadow-elevation-1 [&_svg]:text-current",
        secondary:
          "bg-secondary text-secondary-foreground shadow-elevation-1 hover:shadow-elevation-2 hover:bg-secondary/80 [&_svg]:text-secondary-foreground",
        ghost: "hover:bg-primary/10 hover:text-primary [&_svg]:text-current",
        link: "text-primary underline-offset-4 hover:underline [&_svg]:text-primary",
      },
      size: {
        default: "h-10 px-5 py-2", // Taller and wider for better touch target
        sm: "h-8 rounded-full px-3 text-xs",
        lg: "h-12 rounded-full px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
